import {
  ParlorHeadSection,
  ETypeMainHead,
  CTypeCardView,
  BTypeParlorList,
  DTypeBreadCrumb,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  ParlorDetailProps,
  numberToLocalString,
  BTypeTabs,
  BTypeBinsoPriceSummary,
  colors,
  BTypeCTA,
  excludeParlorList,
  DTypeCTA,
  GOI_WEB_URL,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";
import parlors from "goi_common/src/constants/parlors_detail.json";

import React from "react";

import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = parlors
    .filter((res) => targetSidos.includes(res.sido))
    .map((parlor) => ({
      params: {
        companyname: parlor.companyname,
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const companyname = params?.companyname;
  let parlor;
  let district;
  let name = "";
  let description = "";

  if (companyname) {
    parlor = parlors.find((parlor) => parlor.companyname === companyname);
    if (parlor) {
      name = parlor.companyname;

      district = parlor.address.slice(0, 2);
      if (parlor.address.split(" ")[0].length == 4)
        district = parlor.address[0] + parlor.address[2];

      let binsoString = "";
      parlor.binsos.forEach((binso: any) => {
        binsoString += `${binso.pyeong.toFixed()}평-${numberToLocalString(
          binso.charge
        )}원, `;
      });

      description = `${parlor.companyname}의 빈소는 약 ${
        parlor.binsos[0].pyeong
      }평부터 ${
        parlor.binsos[parlor.binsos.length - 1].pyeong
      }평 까지이며, 하루 비용은 대략 ${parlor.binsos[0].charge.toLocaleString()}원부터 최대로는 ${parlor.binsos[
        parlor.binsos.length - 1
      ].charge.toLocaleString()}원까지 나올 수 있습니다. ${binsoString}, `;
    }
  }

  return {
    props: {
      defaultParlor: parlor,
      pageDescription: description,
      pageKeywords: `${parlor?.companyname}, ${parlor?.companyname} 비용, ${parlor?.companyname}평균 예상 비용, ${parlor?.companyname} 무빈소, ${parlor?.companyname} 가족장, ${parlor?.companyname} 추천,
      ${parlor?.companyname} 빈소, ${parlor?.companyname} 후기, ${parlor?.companyname} 얼마`,
      pageOgTitle: `(${district}) ${parlor?.companyname} 비용 알아보기 | 서울경기인천장례정보포털`,
      pageOgDescription: description,
      pageOgImage: parlor?.images[0]?.url || "",
      hasError: !parlor,
    },
  };
};

export default function price({
  defaultParlor,
  pageOgTitle,
}: {
  defaultParlor: ParlorDetailProps;
  pageOgTitle: string;
}) {
  const regionParlors = parlors.filter((res) => targetSidos.includes(res.sido));
  const sidoName = FUNERAL_SIDO.get(Number(defaultParlor.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultParlor.sido))?.get(
    Number(defaultParlor.sigungu)
  );

  const carouselItems = regionParlors.map((res, i) => {
    return {
      "@type": "ListItem",
      name: `(비용안내) ${res.companyname}`,
      image: `${res.images[0].url}`,
      url: `https://www.metropolitan-funeral.co.kr/parlor-place/${res.companyname}/`,
      position: i + 1,
    };
  });

  const carouselRichSearch =
    regionParlors.length > 6
      ? {
          "@context": "http://schema.org",
          "@type": "ItemList",
          itemListElement: carouselItems,
        }
      : {};
  if (excludeParlorList.includes(String(defaultParlor.id))) {
    return (
      <>
        <Head>
          <title>{pageOgTitle}</title>
        </Head>
        <ParlorHeadSection
          component={
            <>
              <DTypeBreadCrumb
                mainColor={regionColor}
                firstItem={{
                  text: `${sidoName} 장례식장`,
                  href: `/price-funeral/${sidoName} 지역 장례식장 추천/`,
                  event: {
                    domain: "metro",
                    path: "prlr_price",
                    trigger_and_target: "br_crumb_sido_prlr_list",
                  },
                }}
                secondItem={{
                  text: `${sigunguName} 장례식장`,
                  href: `/price-funeral/${sidoName} 지역-${sigunguName} 장례식장 추천/`,
                  event: {
                    domain: "metro",
                    path: "prlr_price",
                    trigger_and_target: "br_crumb_sigungu_prlr_list",
                  },
                }}
                goiHomeEvent={{
                  domain: "metro",
                  path: "prlr_price",
                  trigger_and_target: "br_crumb_goi_home",
                }}
              />
              <ETypeMainHead
                mainColor={regionColor}
                defaultParlor={defaultParlor}
                title={`${defaultParlor.companyname}`}
                managed_type={defaultParlor.managed_type}
                event={{
                  domain: "metro",
                  path: "prlr_price",
                  trigger_and_target: "uv_mth_req",
                }}
              />
            </>
          }
        />
        <Stack>해당 장례식장 요청으로 정보를 공개하지 않습니다.</Stack>
      </>
    );
  }
  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
        {/* {regionParlors.length > 5 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(carouselRichSearch),
            }}
          />
        )} */}
      </Head>
      <ParlorHeadSection
        component={
          <>
            <DTypeBreadCrumb
              mainColor={regionColor}
              firstItem={{
                text: `${sidoName} 장례식장`,
                href: `/price-funeral/${sidoName} 지역 장례식장 추천/`,
              }}
              secondItem={{
                text: `${sigunguName} 장례식장`,
                href: `/price-funeral/${sidoName} 지역-${sigunguName} 장례식장 추천/`,
              }}
            />
            <ETypeMainHead
              mainColor={regionColor}
              defaultParlor={defaultParlor}
              title={`${defaultParlor.companyname} 비용`}
              managed_type={defaultParlor.managed_type}
              event={{
                domain: "metro",
                path: "prlr_price",
                trigger_and_target: "uv_mth_req",
              }}
            />
          </>
        }
      />

      {/* <Box padding="0 20px">
        <Box
          width="100%"
          height="1px"
          style={{ background: colors.gray900 }}
          margin="0 0 20px"
          padding="0 20px"
        />
      </Box> */}
      {/* <BTypeCTA
        mainColor={regionColor}
        event={{
          domain: "metro",
          path: "prlr_price",
          trigger_and_target: "CTA_uv_mth_req",
        }}
      /> */}
      {/* <DTypeCTA
        mainColor={regionColor}
        companyname={defaultParlor.companyname}
        href={`${GOI_WEB_URL}/match/parlor-requests/${defaultParlor.id}/`}
        event={{
          domain: "metro",
          path: "prlr_price",
          trigger_and_target: "CTA_uv_mth_req",
        }}
      /> */}

      <Box padding="0 20px">
        <Box
          width="100%"
          height="1px"
          style={{ background: colors.gray900 }}
          margin="0 0 20px"
          padding="0 20px"
        />
      </Box>

      <BTypeTabs
        mainColor={regionColor}
        href={`parlor-place/${defaultParlor.companyname}`}
        selectTab="비용"
        defaultParlor={defaultParlor}
        event={{
          domain: "metro",
          path: "prlr_price",
          trigger_and_target: "BType_tab",
        }}
      />

      <Stack padding="20px 34px">
        <BTypeBinsoPriceSummary
          defaultParlor={defaultParlor}
          mainColor={regionColor}
          event={{
            domain: "metro",
            path: "prlr_price",
            trigger_and_target: "uv_mth_req_btom",
          }}
        />
      </Stack>
      <Box padding="0 20px">
        <Box
          width="100%"
          height="1px"
          style={{ background: colors.gray900 }}
          margin="0 0 20px"
          padding="0 20px"
        />
      </Box>
      <BTypeParlorList
        sido={defaultParlor.sido}
        title={`주변 ${sidoName} 장례식장`}
        CardViewType={(res) => (
          <CTypeCardView
            sigungu={res.sigungu}
            mainColor={regionColor}
            key={res.companyname}
            name={res.companyname}
            href={`/parlor-place/${res.companyname}/`}
            sido={res.sido}
            imgSrc={res.images[0] ? res.images[0].url : ""}
            event={{
              domain: "metro",
              path: "prlr_price",
              trigger_and_target: "uv_prlr_detail",
            }}
          />
        )}
      />
    </>
  );
}
